import { useEffect, useMemo, useState, type FormEvent } from "react";
import { addDoc, collection, doc, onSnapshot, orderBy, query, updateDoc } from "firebase/firestore";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import { ImageUp } from "lucide-react";
import { paths, STARTER_CATEGORIES, STARTER_TEMPLATES, type DocTemplate, type StarterTemplate } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, cn } from "@clevis/shared-ui";
import { db, storage } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { DocView, blanksIn, useCompanyLogo } from "../../components/DocView";
import { LogoEditor } from "../../components/LogoEditor";
import { CompanyDetailsCard } from "../../components/CompanyDetailsCard";
import { BlankFiller, startingValues, unfilled } from "../../components/BlankFiller";
import { fillBlanks } from "@clevis/shared-types";

// The template library: the company's own documents (service agreements,
// estimate terms, waivers…) by category — the category is where reps find
// them on a job — plus Clevis's starter library. Starters are read-only:
// "Save as" copies one into the company's templates to fill in and edit.

type Row = DocTemplate & { id: string };
type Draft = { id: string | null; name: string; category: string; body: string; requiresSignature: boolean };

const MERGE_FIELDS = [
  "customer.name",
  "customer.company",
  "customer.address",
  "customer.phone",
  "customer.email",
  "job.title",
  "job.address",
  "job.date",
  "job.po",
  "company.name",
  "company.phone",
  "company.email",
  "date",
];

export function TemplatesPage() {
  const { tenantId, role, superAdmin } = useAuth();
  const isAdmin = superAdmin || role === "owner" || role === "admin";
  const logoUrl = useCompanyLogo();
  const [company, setCompany] = useState({ name: "", phone: "", email: "", license: "" });
  const [rows, setRows] = useState<Row[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  // Typed-in values for the draft's blanks (put into the text with "Put them in").
  const [blankValues, setBlankValues] = useState<Record<string, string>>({});
  useEffect(() => {
    setBlankValues(draft ? startingValues(draft.body) : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.id]);
  const [preview, setPreview] = useState<string | null>(null); // "own:<id>" | "starter:<key>"
  const [saveAs, setSaveAs] = useState<{ key: string; name: string } | null>(null); // key as for preview
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(query(collection(db, paths.tenantTemplates(tenantId)), orderBy("name")), (snap) =>
        setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as DocTemplate) }))),
      ),
      onSnapshot(doc(db, paths.tenant(tenantId)), (s) =>
        setCompany({
          name: (s.get("name") as string) ?? "",
          phone: (s.get("contactPhone") as string) ?? "",
          email: (s.get("contactEmail") as string) ?? "",
          license: s.get("printLicenseLines") === false ? "" : ((s.get("licenseLines") as string) ?? ""),
        }),
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId]);

  const live = rows.filter((r) => !r.archivedAt);
  const byCategory = useMemo(() => {
    const m = new Map<string, Row[]>();
    for (const r of live) (m.get(r.category) ?? m.set(r.category, []).get(r.category)!).push(r);
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);
  const sourceOf = (key: string): { name: string; category: string; body: string; requiresSignature: boolean; signer?: "customer" | "company" } | null => {
    if (key.startsWith("starter:")) return STARTER_TEMPLATES.find((s) => s.key === key.slice(8)) ?? null;
    return live.find((r) => r.id === key.slice(4)) ?? null;
  };
  const uniqueName = (name: string) => {
    const taken = new Set(live.map((r) => r.name.toLowerCase()));
    if (!taken.has(name.toLowerCase())) return name;
    for (let n = 2; ; n++) if (!taken.has(`${name} (${n})`.toLowerCase())) return `${name} (${n})`;
  };

  async function create(data: Omit<Draft, "id"> & { signer?: "customer" | "company" }): Promise<string> {
    const r = await addDoc(collection(db, paths.tenantTemplates(tenantId!)), {
      ...(data.signer === "company" ? { signer: "company" } : {}),
      name: data.name.trim().slice(0, 120),
      category: data.category.trim().slice(0, 60) || "Other",
      body: data.body,
      requiresSignature: data.requiresSignature,
      archivedAt: null,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
    return r.id;
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!tenantId || !draft) return;
    setBusy(true);
    setNote("");
    try {
      if (draft.id) {
        await updateDoc(doc(db, paths.tenantTemplate(tenantId, draft.id)), {
          name: draft.name.trim(),
          category: draft.category.trim() || "Other",
          body: draft.body,
          requiresSignature: draft.requiresSignature,
          updatedAt: Date.now(),
        });
      } else await create(draft);
      setDraft(null);
      setNote("Saved.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /** Copy a starter (or one of ours) into our templates, then open it to fill in. */
  async function doSaveAs() {
    if (!tenantId || !saveAs) return;
    const src = saveAs.key === "draft" ? draft : sourceOf(saveAs.key);
    if (!src || !saveAs.name.trim()) return;
    setBusy(true);
    setNote("");
    try {
      const name = uniqueName(saveAs.name.trim());
      const id = await create({ name, category: src.category, body: src.body, requiresSignature: src.requiresSignature, signer: "signer" in src ? src.signer : undefined });
      setSaveAs(null);
      setPreview(null);
      setDraft({ id, name, category: src.category, body: src.body, requiresSignature: src.requiresSignature });
      setShowPreview(true);
      setNote(`Saved as "${name}" in your templates.${blanksIn(src.body).length ? " Fill in the highlighted blanks, then Save." : ""}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function archive(id: string) {
    if (!tenantId) return;
    await updateDoc(doc(db, paths.tenantTemplate(tenantId, id)), { archivedAt: Date.now(), updatedAt: Date.now() });
  }

  const contact = [company.phone, company.email, ...company.license.split(/\r?\n/).map((l) => l.trim())];
  const view = (src: { name: string; body: string; requiresSignature: boolean; signer?: "customer" | "company" }) => (
    <DocView title={src.name} body={src.body} companyName={company.name} companyContact={contact} logoUrl={logoUrl} requiresSignature={src.requiresSignature} signer={src.signer} markBlanks className="mx-4 mb-3" />
  );
  const saveAsForm = (key: string) =>
    saveAs?.key === key ? (
      <form
        className="flex flex-wrap items-end gap-2 bg-cream/50 px-4 py-2"
        onSubmit={(e) => {
          e.preventDefault();
          void doSaveAs();
        }}
      >
        <div className="min-w-56 flex-1">
          <Label>Save as (your copy's name)</Label>
          <Input className="py-1 text-sm" autoFocus value={saveAs.name} maxLength={120} onChange={(e) => setSaveAs({ key, name: e.target.value })} />
        </div>
        <Button type="submit" className="px-3 py-1 text-xs" busy={busy} disabled={!saveAs.name.trim()}>
          Save as
        </Button>
        <Button type="button" variant="ghost" className="px-3 py-1 text-xs" onClick={() => setSaveAs(null)}>
          Cancel
        </Button>
      </form>
    ) : null;
  const rowActions = (key: string, name: string) => (
    <span className="flex shrink-0 items-center gap-3 text-xs">
      <button className="text-steel underline hover:text-ink" onClick={() => setPreview(preview === key ? null : key)}>
        {preview === key ? "Close" : "Preview"}
      </button>
      <button className="text-brandorange-dark underline hover:text-brandorange" onClick={() => setSaveAs({ key, name: key.startsWith("own:") ? `${name} (copy)` : name })}>
        Save as
      </button>
    </span>
  );

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-bold">Templates</h1>
        <Button
          onClick={() => {
            setDraft(draft && !draft.id ? null : { id: null, name: "", category: "", body: "", requiresSignature: true });
            setShowPreview(false);
          }}
        >
          {draft && !draft.id ? "Cancel" : "New template"}
        </Button>
      </div>
      {note ? <p className="text-xs text-steel">{note}</p> : null}

      {isAdmin && tenantId ? <LogoCard tenantId={tenantId} logoUrl={logoUrl} /> : null}
      {isAdmin && tenantId ? <CompanyDetailsCard tenantId={tenantId} /> : null}

      {draft ? (
        <Card>
          <CardTitle>{draft.id ? "Edit template" : "New template"}</CardTitle>
          <form onSubmit={save} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Name</Label>
                <Input required value={draft.name} maxLength={120} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Annual maintenance plan" />
              </div>
              <div>
                <Label>Category (where reps find it)</Label>
                <Input required list="template-categories" value={draft.category} maxLength={60} onChange={(e) => setDraft({ ...draft, category: e.target.value })} placeholder="Service Agreements" />
                <datalist id="template-categories">
                  {[...new Set([...STARTER_CATEGORIES, ...live.map((r) => r.category)])].map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
            </div>
            <div>
              <Label>Document text</Label>
              <textarea
                required
                maxLength={20000}
                className="h-72 w-full rounded-lg border border-steel/30 p-3 text-sm leading-relaxed text-ink focus:border-brandorange focus:outline-none"
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                placeholder={"This agreement is between {{company.name}} and {{customer.name}}…\n\n## What's included\n- One visit each month"}
              />
              <p className="text-[11px] text-steel">
                <span className="font-mono">## Heading</span> starts a section, <span className="font-mono">- item</span> is a bullet, <span className="font-mono">[blank]</span> is something to fill in. Fields fill themselves:
              </p>
              <div className="mt-1 flex flex-wrap gap-1">
                {MERGE_FIELDS.map((f) => (
                  <button key={f} type="button" className="rounded-full bg-cream px-2 py-0.5 font-mono text-[10px] text-steel hover:text-ink" onClick={() => setDraft({ ...draft, body: `${draft.body}{{${f}}}` })}>
                    {f}
                  </button>
                ))}
              </div>
              {blanksIn(draft.body).length ? (
                <div className="mt-2 rounded-lg border border-brandorange/40 bg-cream/40 p-3">
                  <p className="mb-1 text-sm font-semibold text-ink">Fill in the blanks ({blanksIn(draft.body).length})</p>
                  <p className="mb-2 text-xs text-steel">Type your own values; they go into the document. Leave one empty to fill it in each time you send it instead.</p>
                  <BlankFiller body={draft.body} values={blankValues} onChange={setBlankValues} />
                  <div className="mt-2 flex items-center gap-2">
                    <Button type="button" className="px-3 py-1 text-xs" disabled={unfilled(draft.body, blankValues).length === blanksIn(draft.body).length} onClick={() => setDraft({ ...draft, body: fillBlanks(draft.body, blankValues) })}>
                      Put them in
                    </Button>
                    <span className="text-[11px] text-steel">Then Save.</span>
                  </div>
                </div>
              ) : null}
            </div>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={draft.requiresSignature} onChange={(e) => setDraft({ ...draft, requiresSignature: e.target.checked })} />
              The customer signs it
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="submit" busy={busy}>
                Save
              </Button>
              {draft.id ? (
                <Button type="button" variant="ghost" onClick={() => setSaveAs({ key: "draft", name: `${draft.name} (copy)` })}>
                  Save as…
                </Button>
              ) : null}
              <Button type="button" variant="ghost" onClick={() => setShowPreview((v) => !v)}>
                {showPreview ? "Hide preview" : "Preview"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setDraft(null)}>
                Cancel
              </Button>
            </div>
          </form>
          {saveAsForm("draft")}
          {showPreview ? <DocView title={draft.name || "Untitled"} body={draft.body} companyName={company.name} companyContact={contact} logoUrl={logoUrl} requiresSignature={draft.requiresSignature} markBlanks className="mt-3" /> : null}
        </Card>
      ) : null}

      {byCategory.length ? <h2 className="pt-1 text-sm font-semibold text-ink">Your templates</h2> : null}
      {byCategory.map(([category, items]) => (
        <Card key={category} className="p-0">
          <div className="border-b border-steel/15 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-steel">{category}</div>
          {items.map((r) => (
            <div key={r.id} className="border-b border-steel/10 last:border-0">
              <div className="flex items-center gap-3 px-4 py-2">
                <button
                  className="min-w-0 flex-1 text-left"
                  onClick={() => {
                    setDraft({ id: r.id, name: r.name, category: r.category, body: r.body, requiresSignature: r.requiresSignature });
                    setShowPreview(false);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  <div className="truncate text-sm font-medium text-ink">{r.name}</div>
                  <div className="truncate text-xs text-steel">
                    {r.requiresSignature ? "Customer signs" : "No signature"}
                    {blanksIn(r.body).length ? <span className="text-brandorange-dark"> · {blanksIn(r.body).length} blanks to fill in</span> : null}
                  </div>
                </button>
                {rowActions(`own:${r.id}`, r.name)}
                <button className="shrink-0 text-xs text-steel hover:text-danger" onClick={() => void archive(r.id)}>
                  Archive
                </button>
              </div>
              {saveAsForm(`own:${r.id}`)}
              {preview === `own:${r.id}` ? view(r) : null}
            </div>
          ))}
        </Card>
      ))}

      <div className="pt-2">
        <h2 className="text-sm font-semibold text-ink">Starter library</h2>
        <p className="text-xs text-steel">
          Professional starting points. Preview one, then <strong>Save as</strong> to make it yours and fill in the highlighted blanks. They're business
          templates, not legal advice: have your attorney look over anything you rely on.
        </p>
      </div>
      {STARTER_CATEGORIES.map((category) => (
        <Card key={category} className="p-0">
          <div className="border-b border-steel/15 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-steel">{category}</div>
          {STARTER_TEMPLATES.filter((s) => s.category === category).map((s: StarterTemplate) => (
            <div key={s.key} className={cn("border-b border-steel/10 last:border-0", preview === `starter:${s.key}` && "bg-cream/30")}>
              <div className="flex items-center gap-3 px-4 py-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-ink">{s.name}</div>
                  <div className="text-xs text-steel">{s.description}</div>
                </div>
                {rowActions(`starter:${s.key}`, s.name)}
              </div>
              {saveAsForm(`starter:${s.key}`)}
              {preview === `starter:${s.key}` ? view(s) : null}
            </div>
          ))}
        </Card>
      ))}
    </div>
  );
}

/** Admins: the logo on every document. */
function LogoCard({ tenantId, logoUrl }: { tenantId: string; logoUrl: string | null }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [inputKey, setInputKey] = useState(0);
  // The picked image, being fitted to the logo window before it's saved.
  const [editing, setEditing] = useState<File | null>(null);

  function pick(file: File) {
    setMsg("");
    if (!/^image\/(png|jpeg|webp|gif)$/.test(file.type)) return setMsg("Use a PNG or JPG image.");
    if (file.size > 15 * 1024 * 1024) return setMsg("That file is very large. Use one under 15 MB.");
    setEditing(file);
    setInputKey((k) => k + 1);
  }

  async function upload(png: Blob) {
    setMsg("");
    setBusy(true);
    try {
      const path = `tenants/${tenantId}/branding/logo-${Date.now()}.png`;
      await uploadBytes(storageRef(storage, path), png, { contentType: "image/png" });
      await call("setCompanyLogo", { tenantId, storagePath: path });
      setMsg("Logo saved. It's on your estimates, invoices and documents now.");
      setEditing(null);
    } catch (err) {
      setMsg((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    setBusy(true);
    try {
      await call("setCompanyLogo", { tenantId, storagePath: null });
      setMsg("Logo removed. Documents show your company name instead.");
    } catch (err) {
      setMsg((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="flex flex-wrap items-center gap-4">
      <div className="flex h-16 w-44 shrink-0 items-center justify-center rounded-lg border border-dashed border-steel/40 bg-cream/40">
        {logoUrl ? <img src={logoUrl} alt="Your logo" className="max-h-14 max-w-[164px] object-contain" /> : <span className="flex items-center gap-1 text-xs text-steel"><ImageUp size={14} /> Your logo</span>}
      </div>
      <div className="min-w-48 flex-1">
        <p className="text-sm font-semibold text-ink">Logo on your documents</p>
        <p className="text-xs text-steel">
          Shows at the top of estimates, invoices, agreements and the pages customers sign. Best: a wide logo, 3 wide by 1 tall, like <strong className="text-ink">900 × 300 pixels</strong> (600 × 200 works too), PNG with a clear background. Any other size: pick it, then crop, zoom or stretch it to fit.
        </p>
        {msg ? <p className="mt-1 text-xs text-ink">{msg}</p> : null}
      </div>
      <div className="flex items-center gap-2">
        <label className={cn("cursor-pointer rounded-lg bg-brandorange px-3 py-1.5 text-xs font-semibold text-white hover:bg-brandorange-dark", busy && "pointer-events-none opacity-60")}>
          {busy ? "Saving…" : logoUrl ? "Replace" : "Upload logo"}
          <input key={inputKey} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => e.target.files?.[0] && pick(e.target.files[0])} />
        </label>
        {logoUrl ? (
          <button className="text-xs text-steel hover:text-danger" disabled={busy} onClick={() => void remove()}>
            Remove
          </button>
        ) : null}
      </div>
      {editing ? <LogoEditor file={editing} busy={busy} onCancel={() => setEditing(null)} onSave={(png) => void upload(png)} /> : null}
    </Card>
  );
}
